import * as React from 'react';
import {
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	MoneyFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerSelectedGdsServicesFragment
} from '@websky/graphql';
import { UpsaleItem, UpsaleService as UpsaleServiceType } from '@features/upsale';
import BaggageIcon from '../../../../../Baggage/Baggage/components/BaggageIcon/BaggageIcon';
import { convertBaggageObject } from '../../../../../Checkout/types';
import CdnImage from '../../../../../CdnImage';
import { isInsuranceGuard } from '@widgets/upsale/lib/guards';
import { EventCategory, Log } from '../../../../../sentry';
import { ServiceDescription, PassengerService } from '../../lib/types';

interface Props {
	service: ServiceDescription;
	preselectedService: PassengerService;
	className?: string;
}

const UpsaleService: React.FC<Props> = ({ service, preselectedService, className }) => {
	const BASE_PATH = 'upsale-services';

	if (isInsuranceGuard(service)) {
		const insurance = service;

		const serviceItem: UpsaleServiceType = {
			header: (
				<CdnImage
					contentRepo
					path={`${BASE_PATH}/${insurance.code}.png`}
					fallbackUrl={`${BASE_PATH}/insurance_upsale_default.png`}
				/>
			),
			name: insurance.name,
			description: insurance.description,
			price: insurance.price
		};

		return <UpsaleItem className={className} service={serviceItem} />;
	}

	const preselectedGdsService = preselectedService as PassengerSelectedGdsServicesFragment;

	let preselectedServicePrice: MoneyFragment = null;

	try {
		preselectedServicePrice = {
			amount: preselectedGdsService.count * preselectedGdsService.segmentIds.length * service.price.amount,
			currency: service.price.currency
		};
	} catch (e) {
		//  why there is no price?
		Log({
			category: EventCategory.Backend,
			message: `PreselectedServiceWithoutPrice: ${preselectedGdsService?.serviceId}`
		});
	}

	if (service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
		const baggageService = service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;
		const serviceItem: UpsaleServiceType = {
			header: <BaggageIcon realSize={true} baggage={convertBaggageObject(baggageService)} />,
			name: baggageService.name,
			description: baggageService.description,
			price: preselectedServicePrice
		};

		return <UpsaleItem className={className} service={serviceItem} />;
	}

	if (service.type === OrderAdditionalServiceGdsServiceServiceType.Meal) {
		const mealService = service as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment;

		const serviceItem: UpsaleServiceType = {
			header: <img alt={mealService.name} src={mealService.image} />,
			name: mealService.name,
			description: mealService.description,
			price: mealService.price
		};

		return <UpsaleItem className={className} service={serviceItem} />;
	}

	if (service.type === OrderAdditionalServiceGdsServiceServiceType.Misc) {
		const miscService = service as GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;
		const serviceItem: UpsaleServiceType = {
			header: <img src={miscService.image} alt={miscService.name} />,
			name: miscService.name,
			description: miscService.description,
			price: preselectedServicePrice
		};

		return <UpsaleItem className={className} service={serviceItem} />;
	}

	if (service.type === OrderAdditionalServiceGdsServiceServiceType.Vip) {
		const vipService = service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

		const serviceItem: UpsaleServiceType = {
			header: (
				<CdnImage
					path={`${BASE_PATH}/${vipService.rfisc}.png`}
					fallbackUrl={`${BASE_PATH}/vip_default.png`}
					contentRepo={true}
				/>
			),
			name: vipService.name,
			description: vipService.description,
			price: preselectedServicePrice
		};

		return <UpsaleItem className={className} service={serviceItem} />;
	}

	Log({
		category: EventCategory.Ui,
		message: `unknown service ${service?.name}`
	});

	return null;
};

export default UpsaleService;
